import { LayoutGrid, List } from 'lucide-react';
import type { FilesViewMode } from '../../../lib/files-url-state';
import { useTranslation } from '../../../lib/i18n';
import { IconButton } from '../ui';
import styles from './FilesViewToggle.module.css';

// List or thumbnails. Two states, so it is two glyphs rather than a picker: the one you are in reads as pressed.
//
// The choice is PERSISTED in the URL with the rest of the browser state, which makes it survive a refresh and
// travel in a shared link — a person who prefers thumbnails prefers them on the second visit too. It is
// deliberately NOT reset by "Clear filters": how you like to look at files is a preference, not part of the
// question you asked of them.
export function FilesViewToggle({
  view,
  onChange,
  className,
}: {
  view: FilesViewMode;
  onChange: (view: FilesViewMode) => void;
  /** Layout from the enclosing row — the switch owns its two glyphs, not its place on the page. */
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={className ? `${styles.toggle} ${className}` : styles.toggle}
      role="group"
      aria-label={t('files.view.label')}
    >
      <IconButton
        size="small"
        aria-label={t('files.view.list')}
        aria-pressed={view === 'list'}
        data-active={view === 'list' || undefined}
        onClick={() => onChange('list')}
      >
        <List size={16} aria-hidden />
      </IconButton>
      <IconButton
        size="small"
        aria-label={t('files.view.thumbnails')}
        aria-pressed={view === 'thumbnails'}
        data-active={view === 'thumbnails' || undefined}
        onClick={() => onChange('thumbnails')}
      >
        <LayoutGrid size={16} aria-hidden />
      </IconButton>
    </div>
  );
}
